// TransferPicker.tsx — widget del step 'ai-transfer' (AI Transfer).
// Qué hace: en el rail queda un disparador compacto con lo elegido; la elección
//   real ocurre en TransferModal, a pantalla grande. Elegir la referencia y las
//   fotos es una decisión visual y en 392px no se veía nada.
// Qué exporta: TransferPicker.
// Quién lo usa: NovahGenerateChat.

import { useEffect, useRef, useState } from 'react';
import { BakedStyleOption } from '../types';
import { WidgetWrap, ConfirmBtn } from '../styled';
import { TriggerCard, TriggerStrip, TriggerText } from '../styleModalStyled';
import { TransferModal } from './TransferModal';

interface Props {
  bakedStyles: BakedStyleOption[];         // catálogo de referencias (usan .preview)
  transferRefUrl: string;                  // referencia elegida (preview o subida)
  selectReference: (url: string) => void;
  uploadReference: (file: File) => void;   // subir una referencia propia
  refUploading: boolean;
  myPhotos: { id: string; url: string; name?: string }[];
  selectedPhotoIds: string[];
  togglePhoto: (id: string) => void;
  useLogo: boolean;
  setUseLogo: (v: boolean) => void;
  hasLogo: boolean;                        // ¿el workspace tiene logo?
  confirmTransfer: () => void;
  totalPosts: number;
  uploadPhoto: (file: File) => void;
  photoUploading: boolean;
  myAds: { id: string; url: string; label: string }[];
  varyLayout: boolean;
  setVaryLayout: (v: boolean) => void;
  instructions: string;
  setInstructions: (v: string) => void;
}

export function TransferPicker(props: Props) {
  const { transferRefUrl, selectedPhotoIds, confirmTransfer, myPhotos, bakedStyles } = props;
  const [modalOpen, setModalOpen] = useState(false);

  // Se abre solo la primera vez: es LA decisión del paso. Si el usuario lo
  // cierra no vuelve a abrirse — se reabre con el disparador.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current) return;
    if (bakedStyles.length === 0) return; // catálogo todavía cargando
    autoOpened.current = true;
    setModalOpen(true);
  }, [bakedStyles.length]);

  const pickedPhotos = myPhotos.filter((p) => selectedPhotoIds.includes(p.id));
  const nPhotos = pickedPhotos.length;

  return (
    <WidgetWrap>
      <TriggerCard type="button" onClick={() => setModalOpen(true)}>
        <TriggerStrip>
          {transferRefUrl && <img src={transferRefUrl} alt="" loading="lazy" />}
          {pickedPhotos.slice(0, 2).map((p) => (
            <img key={p.id} src={p.url} alt="" loading="lazy" />
          ))}
        </TriggerStrip>
        <TriggerText>
          {transferRefUrl
            ? `Reference + ${nPhotos} photo${nPhotos !== 1 ? 's' : ''}`
            : 'Pick a reference ad'}
          <em>
            {transferRefUrl
              ? 'Tap to change the reference, photos or logo'
              : props.varyLayout
                ? 'Tap to open — the AI borrows its look'
                : 'Tap to open — the AI copies its design'}
          </em>
        </TriggerText>
      </TriggerCard>

      <ConfirmBtn onClick={confirmTransfer} disabled={!transferRefUrl} style={{ marginTop: 10 }}>
        {!transferRefUrl
          ? 'Pick a reference'
          : nPhotos === 0
            ? 'Generate from reference'
            : `Combine with ${nPhotos} photo${nPhotos !== 1 ? 's' : ''}`}
      </ConfirmBtn>

      <TransferModal {...props} open={modalOpen} onClose={() => setModalOpen(false)} />
    </WidgetWrap>
  );
}
